import { useEffect, useRef } from 'react';

// Lightweight keyboard shortcuts. Supports single keys and Gmail-style sequences
// (e.g. 'g t' means press 'g' then 't' within 800ms).
//
// Usage:
//   useHotkeys({
//     'n': () => openNew(),
//     'g t': () => navigate('/today'),
//     'g h': () => navigate('/habits'),
//     '?': () => setHelpOpen(true),
//   });
//
// Modifier keys (ctrl/cmd/alt) and inputs are ignored to avoid surprises.

const isTypingInElement = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
};

export const useHotkeys = (map, deps = []) => {
  const sequenceRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingInElement(e.target)) return;
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;

      // Build sequence
      sequenceRef.current.push(key);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => { sequenceRef.current = []; }, 800);

      // Try longest match first
      for (let len = Math.min(sequenceRef.current.length, 3); len >= 1; len--) {
        const candidate = sequenceRef.current.slice(-len).join(' ');
        if (map[candidate]) {
          e.preventDefault();
          sequenceRef.current = [];
          if (timerRef.current) clearTimeout(timerRef.current);
          map[candidate](e);
          return;
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};
